import { useState, useEffect, useCallback } from 'react';
import { getClients, createClient, updateClient, toggleClientActive, getClientLedger, getClientTransactions } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { AlertTriangle, Printer, Download } from 'lucide-react';
import BalanceChip, { formatCurrency } from '../components/BalanceChip';
import { toast } from 'sonner';
import { Plus, Search, User, Mail, Phone, FileText, Building2, UserX, Bell, ArrowUpDown, Calendar, Receipt } from 'lucide-react';
import { format, parseISO } from 'date-fns';

// Simple debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'AU' },
  { code: '+64', country: 'NZ' },
  { code: '+49', country: 'DE' },
  { code: '+33', country: 'FR' },
  { code: '+81', country: 'JP' },
];

const getDefaultCountryCode = () => {
  const locale = navigator.language || 'en-US';
  const map = { 'en-US': '+1', 'en-CA': '+1', 'en-GB': '+44', 'en-AU': '+61' };
  return map[locale] || '+1';
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sortBy, setSortBy] = useState('name_asc');
  const [filterType, setFilterType] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txnBusiness, setTxnBusiness] = useState({});
  const [txnDateFrom, setTxnDateFrom] = useState('');
  const [txnDateTo, setTxnDateTo] = useState('');
  
  // Deactivation warning dialog
  const [deactivateWarningOpen, setDeactivateWarningOpen] = useState(false);
  const [clientToDeactivate, setClientToDeactivate] = useState(null);
  const [futureBookingsCount, setFutureBookingsCount] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    client_type: 'individual',
    email: '',
    phone_country_code: getDefaultCountryCode(),
    phone_number: '',
    notes: '',
    business_name: '',
    abn_tax_id: '',
    contact_person: '',
    notifications_enabled: true,
    reminder_hours_before: 24,
    notification_email: true,
    notification_sms: false,
    invoice_mode: 'manual',
    auto_invoice_frequency: 'weekly',
    auto_invoice_day: 1,
    auto_invoice_time: '09:00',
    // Per-client invoice overrides
    override_gst_mode: false,
    gst_mode: 'none',
    override_payment_terms: false,
    payment_terms: 'on_receipt',
    custom_payment_terms_days: 30,
  });

  const fetchClients = useCallback(async () => {
    try {
      const params = { sort_by: sortBy };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterType !== 'all') params.client_type = filterType;
      if (!showInactive) params.is_active = true;
      
      const data = await getClients(params);
      setClients(data);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [sortBy, debouncedSearch, filterType, showInactive]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleOpenSheet = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        client_type: client.client_type || 'individual',
        email: client.email || '',
        phone_country_code: client.phone_country_code || getDefaultCountryCode(),
        phone_number: client.phone_number || '',
        notes: client.notes || '',
        business_name: client.business_name || '',
        abn_tax_id: client.abn_tax_id || '',
        contact_person: client.contact_person || '',
        notifications_enabled: client.notifications_enabled ?? true,
        reminder_hours_before: client.reminder_hours_before || 24,
        notification_email: client.notification_email ?? true,
        notification_sms: client.notification_sms ?? false,
        invoice_mode: client.invoice_mode || 'manual',
        auto_invoice_frequency: client.auto_invoice_frequency || 'weekly',
        auto_invoice_day: client.auto_invoice_day || 1,
        auto_invoice_time: client.auto_invoice_time || '09:00',
        // Per-client invoice overrides
        override_gst_mode: client.override_gst_mode ?? false,
        gst_mode: client.gst_mode || 'none',
        override_payment_terms: client.override_payment_terms ?? false,
        payment_terms: client.payment_terms || 'on_receipt',
        custom_payment_terms_days: client.custom_payment_terms_days || 30,
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '', client_type: 'individual', email: '', phone_country_code: getDefaultCountryCode(),
        phone_number: '', notes: '', business_name: '', abn_tax_id: '', contact_person: '',
        notifications_enabled: true, reminder_hours_before: 24, notification_email: true, notification_sms: false,
        invoice_mode: 'manual', auto_invoice_frequency: 'weekly', auto_invoice_day: 1, auto_invoice_time: '09:00',
        override_gst_mode: false, gst_mode: 'none', override_payment_terms: false, payment_terms: 'on_receipt', custom_payment_terms_days: 30,
      });
    }
    setSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean empty strings to null for optional fields
      const cleaned = { ...formData };
      if (!cleaned.email) cleaned.email = null;
      if (!cleaned.phone_number) cleaned.phone_number = null;
      if (!cleaned.phone_country_code) cleaned.phone_country_code = null;
      if (!cleaned.notes) cleaned.notes = null;
      if (!cleaned.business_name) cleaned.business_name = null;
      if (!cleaned.abn_tax_id) cleaned.abn_tax_id = null;
      if (!cleaned.contact_person) cleaned.contact_person = null;

      if (editingClient) {
        await updateClient(editingClient.id, cleaned);
        toast.success('Client updated');
      } else {
        await createClient(cleaned);
        toast.success('Client created');
      }
      setSheetOpen(false);
      fetchClients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to save client';
      toast.error(msg);
    }
  };

  const handleToggleActive = async (client, force = false) => {
    try {
      const result = await toggleClientActive(client.id, force);
      
      if (result.warning && !force) {
        // Show warning dialog
        setClientToDeactivate(client);
        setFutureBookingsCount(result.future_bookings);
        setDeactivateWarningOpen(true);
        return;
      }
      
      toast.success(result.is_active ? 'Client activated' : 'Client deactivated');
      fetchClients();
      if (selectedClient?.id === client.id) {
        setSelectedClient({ ...selectedClient, is_active: result.is_active });
      }
    } catch (err) {
      toast.error('Failed to update client');
    }
  };

  const handleForceDeactivate = async () => {
    if (!clientToDeactivate) return;
    setDeactivateWarningOpen(false);
    await handleToggleActive(clientToDeactivate, true);
    setClientToDeactivate(null);
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setTxnDateFrom('');
    setTxnDateTo('');
    try {
      const data = await getClientTransactions(client.id);
      setTransactions(data.transactions || []);
      setTxnBusiness(data.business || {});
    } catch {
      toast.error('Failed to load transactions');
      setTransactions([]);
    }
  };

  const fetchFilteredTransactions = async () => {
    if (!selectedClient) return;
    try {
      const params = {};
      if (txnDateFrom) params.from_date = txnDateFrom;
      if (txnDateTo) params.to_date = txnDateTo;
      const data = await getClientTransactions(selectedClient.id, params);
      setTransactions(data.transactions || []);
    } catch {
      toast.error('Failed to filter transactions');
    }
  };

  const exportTransactionsCSV = () => {
    if (!transactions.length) return;
    const biz = txnBusiness;
    let csv = '';
    // Business header
    if (biz.name) csv += `${biz.name}\n`;
    if (biz.address) csv += `${biz.address}\n`;
    if (biz.phone) csv += `Phone: ${biz.phone}\n`;
    if (biz.gst_number) csv += `GST: ${biz.gst_number}\n`;
    csv += `\nStatement for: ${selectedClient.business_name || selectedClient.name}\n`;
    if (txnDateFrom || txnDateTo) csv += `Period: ${txnDateFrom || 'Start'} to ${txnDateTo || 'Present'}\n`;
    csv += '\nDate,Type,Invoice,Service,Description,Amount\n';
    
    transactions.forEach(t => {
      const date = t.date ? format(parseISO(t.date), 'yyyy-MM-dd') : '';
      const amt = (t.amount_cents / 100).toFixed(2);
      csv += `${date},${t.type},${t.invoice_number || ''},${(t.service || '').replace(',', ';')},${(t.description || '').replace(',', ';')},${amt}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement-${(selectedClient.business_name || selectedClient.name).replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printTransactions = () => {
    const biz = txnBusiness;
    const clientName = selectedClient.business_name || selectedClient.name;
    let html = `<html><head><title>Statement - ${clientName}</title>
    <style>body{font-family:system-ui,sans-serif;padding:2rem;color:#1a1a1a;max-width:800px;margin:0 auto}
    h1{font-size:1.5rem;margin:0}h2{font-size:1.1rem;color:#666;margin:0.5rem 0}
    .header{margin-bottom:1.5rem;border-bottom:2px solid #333;padding-bottom:1rem}
    .biz-info{color:#666;font-size:0.85rem;line-height:1.4}
    table{width:100%;border-collapse:collapse;margin-top:1rem;font-size:0.85rem}
    th{text-align:left;padding:0.5rem 0.75rem;border-bottom:2px solid #ddd;font-weight:600}
    td{padding:0.4rem 0.75rem;border-bottom:1px solid #eee}
    .amount{text-align:right;font-family:monospace}
    .positive{color:#dc2626}.negative{color:#059669}
    .type-badge{display:inline-block;padding:0.1rem 0.4rem;border-radius:4px;font-size:0.75rem;text-transform:capitalize}
    .type-invoice{background:#f3e8ff;color:#7c3aed}.type-payment{background:#d1fae5;color:#059669}.type-credit{background:#ccfbf1;color:#0d9488}
    .footer{margin-top:2rem;padding-top:1rem;border-top:1px solid #ddd;font-size:0.8rem;color:#999}
    @media print{body{padding:0}}</style></head><body>`;
    
    html += `<div class="header">`;
    if (biz.name) html += `<h1>${biz.name}</h1>`;
    html += `<div class="biz-info">`;
    if (biz.address) html += `${biz.address}<br>`;
    if (biz.phone) html += `Phone: ${biz.phone}<br>`;
    if (biz.gst_number) html += `GST: ${biz.gst_number}`;
    html += `</div></div>`;
    
    html += `<h2>Statement for ${clientName}</h2>`;
    if (txnDateFrom || txnDateTo) html += `<p style="color:#666;font-size:0.85rem">Period: ${txnDateFrom || 'Start'} — ${txnDateTo || 'Present'}</p>`;
    
    html += `<table><thead><tr><th>Date</th><th>Type</th><th>Invoice</th><th>Service</th><th class="amount">Amount</th></tr></thead><tbody>`;
    
    let runningTotal = 0;
    transactions.forEach(t => {
      const date = t.date ? format(parseISO(t.date), 'MMM d, yyyy') : '';
      const amtNum = t.amount_cents / 100;
      runningTotal += t.amount_cents;
      const cls = t.amount_cents > 0 ? 'positive' : 'negative';
      html += `<tr><td>${date}</td><td><span class="type-badge type-${t.type}">${t.type}</span></td><td>${t.invoice_number || '-'}</td><td>${t.service || '-'}</td><td class="amount ${cls}">${amtNum > 0 ? '' : '-'}$${Math.abs(amtNum).toFixed(2)}</td></tr>`;
    });
    
    html += `</tbody></table>`;
    html += `<div class="footer">Balance: $${(runningTotal / 100).toFixed(2)}</div>`;
    html += `</body></html>`;
    
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  return (
    <div className="flex flex-col h-full" data-testid="clients-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-heading font-bold">Clients</h1>
          <Button onClick={() => handleOpenSheet()} data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-2" /> New Client
          </Button>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search clients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40"><ArrowUpDown className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="recent">Recently Added</SelectItem>
              <SelectItem value="balance">By Balance</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
            <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
          </div>
        </div>
      </div>

      {/* Deactivation Warning Dialog */}
      <Dialog open={deactivateWarningOpen} onOpenChange={setDeactivateWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Cannot Deactivate Client
            </DialogTitle>
            <DialogDescription>
              <strong>{clientToDeactivate?.name}</strong> has <strong>{futureBookingsCount}</strong> future booking(s) scheduled.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Deactivating this client will not cancel their bookings. You may want to reschedule or cancel their bookings first.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateWarningOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleForceDeactivate}>Deactivate Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>{editingClient ? 'Edit Client' : 'New Client'}</SheetTitle></SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            <Tabs value={formData.client_type} onValueChange={v => setFormData(p => ({ ...p, client_type: v }))}>
              <TabsList className="w-full">
                <TabsTrigger value="individual" className="flex-1"><User className="w-4 h-4 mr-2" />Individual</TabsTrigger>
                <TabsTrigger value="business" className="flex-1"><Building2 className="w-4 h-4 mr-2" />Business</TabsTrigger>
              </TabsList>
            </Tabs>

            {formData.client_type === 'business' && (
              <>
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input value={formData.business_name} onChange={e => setFormData(p => ({ ...p, business_name: e.target.value }))} required placeholder="ABC Corporation" />
                </div>
                <div className="space-y-2">
                  <Label>ABN / Tax ID</Label>
                  <Input value={formData.abn_tax_id} onChange={e => setFormData(p => ({ ...p, abn_tax_id: e.target.value }))} placeholder="12 345 678 901" />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>{formData.client_type === 'business' ? 'Contact Person' : 'Name'} *</Label>
              <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} required placeholder={formData.client_type === 'business' ? 'John Smith (CEO)' : 'John Smith'} />
            </div>

            {formData.client_type === 'business' && (
              <p className="text-xs text-muted-foreground -mt-2">The contact person who will receive invoices and reminders</p>
            )}

            <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} /></div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <div className="flex gap-2">
                <Select value={formData.phone_country_code} onValueChange={v => setFormData(p => ({ ...p, phone_country_code: v }))}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRY_CODES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={formData.phone_number} onChange={e => setFormData(p => ({ ...p, phone_number: e.target.value }))} placeholder="Phone number" className="flex-1" />
              </div>
            </div>

            <div className="space-y-2"><Label>Notes</Label><Textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} /></div>

            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center gap-2"><Bell className="w-4 h-4" /><Label className="font-medium">Notifications</Label></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Enable reminders</Label><Switch checked={formData.notifications_enabled} onCheckedChange={v => setFormData(p => ({ ...p, notifications_enabled: v }))} /></div>
              {formData.notifications_enabled && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm">Remind before</Label>
                    <Select value={formData.reminder_hours_before.toString()} onValueChange={v => setFormData(p => ({ ...p, reminder_hours_before: parseInt(v) }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="2">2 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between"><Label className="text-sm">Email</Label><Switch checked={formData.notification_email} onCheckedChange={v => setFormData(p => ({ ...p, notification_email: v }))} /></div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">SMS</Label>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Bõkr Plus</span>
                    </div>
                    <Switch checked={false} disabled className="opacity-50" />
                  </div>
                </>
              )}
            </div>

            {/* Auto-Invoicing Settings */}
            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center gap-2"><Receipt className="w-4 h-4" /><Label className="font-medium">Invoicing</Label></div>
              <div className="space-y-2">
                <Label className="text-sm">Invoice Mode</Label>
                <Select value={formData.invoice_mode} onValueChange={v => setFormData(p => ({ ...p, invoice_mode: v }))}>
                  <SelectTrigger data-testid="invoice-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual - Create invoices manually</SelectItem>
                    <SelectItem value="auto">Automatic - Generate on schedule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.invoice_mode === 'auto' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm">Frequency</Label>
                    <Select value={formData.auto_invoice_frequency} onValueChange={v => setFormData(p => ({ ...p, auto_invoice_frequency: v }))}>
                      <SelectTrigger data-testid="invoice-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="last_day_of_month">Last day of month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.auto_invoice_frequency !== 'last_day_of_month' && (
                    <div className="space-y-2">
                      <Label className="text-sm">
                        {formData.auto_invoice_frequency === 'monthly' ? 'Day of Month' : 'Day of Week'}
                      </Label>
                      <Select 
                        value={formData.auto_invoice_day?.toString() || '1'} 
                        onValueChange={v => setFormData(p => ({ ...p, auto_invoice_day: parseInt(v) }))}
                      >
                        <SelectTrigger data-testid="invoice-day-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {formData.auto_invoice_frequency === 'monthly' ? (
                            Array.from({ length: 28 }, (_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}</SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="0">Sunday</SelectItem>
                              <SelectItem value="1">Monday</SelectItem>
                              <SelectItem value="2">Tuesday</SelectItem>
                              <SelectItem value="3">Wednesday</SelectItem>
                              <SelectItem value="4">Thursday</SelectItem>
                              <SelectItem value="5">Friday</SelectItem>
                              <SelectItem value="6">Saturday</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-sm">Time</Label>
                    <Select 
                      value={formData.auto_invoice_time || '09:00'} 
                      onValueChange={v => setFormData(p => ({ ...p, auto_invoice_time: v }))}
                    >
                      <SelectTrigger data-testid="invoice-time-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="06:00">6:00 AM</SelectItem>
                        <SelectItem value="07:00">7:00 AM</SelectItem>
                        <SelectItem value="08:00">8:00 AM</SelectItem>
                        <SelectItem value="09:00">9:00 AM</SelectItem>
                        <SelectItem value="10:00">10:00 AM</SelectItem>
                        <SelectItem value="11:00">11:00 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM</SelectItem>
                        <SelectItem value="13:00">1:00 PM</SelectItem>
                        <SelectItem value="14:00">2:00 PM</SelectItem>
                        <SelectItem value="15:00">3:00 PM</SelectItem>
                        <SelectItem value="16:00">4:00 PM</SelectItem>
                        <SelectItem value="17:00">5:00 PM</SelectItem>
                        <SelectItem value="18:00">6:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Invoices will be automatically generated for all unbilled bookings on this schedule.
                  </p>
                </>
              )}

              {/* Per-client Invoice Overrides */}
              <Separator className="my-3" />
              <p className="text-sm font-medium">Client-Specific Overrides</p>
              
              <div className="flex items-center justify-between">
                <Label className="text-sm">Override Payment Terms</Label>
                <Switch 
                  checked={formData.override_payment_terms} 
                  onCheckedChange={v => setFormData(p => ({ ...p, override_payment_terms: v }))} 
                />
              </div>
              {formData.override_payment_terms && (
                <div className="space-y-2 ml-2 pl-3 border-l-2">
                  <Select value={formData.payment_terms} onValueChange={v => setFormData(p => ({ ...p, payment_terms: v }))}>
                    <SelectTrigger data-testid="client-payment-terms-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_receipt">Due on Receipt</SelectItem>
                      <SelectItem value="7">Net 7 (Due in 7 days)</SelectItem>
                      <SelectItem value="14">Net 14 (Due in 14 days)</SelectItem>
                      <SelectItem value="30">Net 30 (Due in 30 days)</SelectItem>
                      <SelectItem value="60">Net 60 (Due in 60 days)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.payment_terms === 'custom' && (
                    <Input 
                      type="number" 
                      min="1" 
                      max="365"
                      value={formData.custom_payment_terms_days} 
                      onChange={e => setFormData(p => ({ ...p, custom_payment_terms_days: parseInt(e.target.value) || 30 }))} 
                      placeholder="Days"
                    />
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label className="text-sm">Override GST Treatment</Label>
                <Switch 
                  checked={formData.override_gst_mode} 
                  onCheckedChange={v => setFormData(p => ({ ...p, override_gst_mode: v }))} 
                />
              </div>
              {formData.override_gst_mode && (
                <div className="space-y-2 ml-2 pl-3 border-l-2">
                  <Select value={formData.gst_mode} onValueChange={v => setFormData(p => ({ ...p, gst_mode: v }))}>
                    <SelectTrigger data-testid="client-gst-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inclusive">Inclusive (prices include GST)</SelectItem>
                      <SelectItem value="exclusive">Exclusive (GST added on top)</SelectItem>
                      <SelectItem value="none">No GST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full">{editingClient ? 'Update' : 'Create'} Client</Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Client list */}
        <div className="w-1/2 border-r overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-muted-foreground py-12">Loading...</div>
          ) : clients.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">{searchQuery ? 'No clients found' : 'No clients yet'}</div>
          ) : (
            <div className="space-y-3">
              {clients.map(client => (
                <Card
                  key={client.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${selectedClient?.id === client.id ? 'ring-2 ring-primary' : ''} ${client.is_active === false ? 'opacity-60' : ''}`}
                  onClick={() => handleSelectClient(client)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${client.client_type === 'business' ? 'bg-blue-500/10' : 'bg-muted'}`}>
                          {client.client_type === 'business' ? <Building2 className="w-5 h-5 text-blue-600" /> : <User className="w-5 h-5 text-muted-foreground" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            {client.client_type === 'business' && client.business_name ? (
                              <h3 className="font-medium">{client.business_name}</h3>
                            ) : (
                              <h3 className="font-medium">{client.name}</h3>
                            )}
                            {client.is_active === false && <Badge variant="secondary">Inactive</Badge>}
                          </div>
                          {client.client_type === 'business' && client.business_name && (
                            <p className="text-sm text-muted-foreground">{client.contact_person || client.name}</p>
                          )}
                        </div>
                      </div>
                      <BalanceChip balanceCents={client.balance_cents_cached} />
                    </div>
                    
                    {/* Future bookings indicator */}
                    {client.future_booking_count > 0 && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span className="text-sm text-muted-foreground">
                          {client.future_booking_count} upcoming ({formatCurrency(client.future_booking_total_cents)})
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Client details */}
        <div className="w-1/2 overflow-y-auto p-6">
          {selectedClient ? (
            <div className="space-y-6 animate-in">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {selectedClient.client_type === 'business' && selectedClient.business_name ? (
                      <h2 className="text-xl font-heading font-bold">{selectedClient.business_name}</h2>
                    ) : (
                      <h2 className="text-xl font-heading font-bold">{selectedClient.name}</h2>
                    )}
                    {selectedClient.is_active === false && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  {selectedClient.client_type === 'business' && selectedClient.business_name && (
                    <p className="text-muted-foreground">{selectedClient.contact_person || selectedClient.name}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleOpenSheet(selectedClient)}>Edit</Button>
                  <Button variant={selectedClient.is_active === false ? 'default' : 'outline'} size="sm" onClick={() => handleToggleActive(selectedClient)}>
                    <UserX className="w-4 h-4 mr-1" />
                    {selectedClient.is_active === false ? 'Activate' : 'Deactivate'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {selectedClient.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" />{selectedClient.email}</div>}
                {selectedClient.phone_number && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" />{selectedClient.phone_country_code} {selectedClient.phone_number}</div>}
                {selectedClient.abn_tax_id && <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-muted-foreground" />ABN: {selectedClient.abn_tax_id}</div>}
              </div>

              {/* Balance & Future Bookings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase mb-2">Balance</h3>
                  <BalanceChip balanceCents={selectedClient.balance_cents_cached} size="lg" />
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase mb-2">Upcoming</h3>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="text-lg font-bold">{selectedClient.future_booking_count || 0}</span>
                    <span className="text-sm text-muted-foreground">
                      ({formatCurrency(selectedClient.future_booking_total_cents || 0)})
                    </span>
                  </div>
                </div>
              </div>

              {selectedClient.notes && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground uppercase mb-2">Notes</h3>
                  <p className="text-sm">{selectedClient.notes}</p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase">Transaction History</h3>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={printTransactions} disabled={!transactions.length} data-testid="print-transactions"><Printer className="w-3 h-3 mr-1" /> Print</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={exportTransactionsCSV} disabled={!transactions.length} data-testid="export-transactions"><Download className="w-3 h-3 mr-1" /> Export</Button>
                  </div>
                </div>

                {/* Date range filter */}
                <div className="flex items-center gap-2 mb-3">
                  <Input type="date" value={txnDateFrom} onChange={(e) => setTxnDateFrom(e.target.value)} className="h-8 text-xs w-36" placeholder="From" data-testid="txn-date-from" />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input type="date" value={txnDateTo} onChange={(e) => setTxnDateTo(e.target.value)} className="h-8 text-xs w-36" placeholder="To" data-testid="txn-date-to" />
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={fetchFilteredTransactions}>Filter</Button>
                  {(txnDateFrom || txnDateTo) && (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setTxnDateFrom(''); setTxnDateTo(''); handleSelectClient(selectedClient); }}>Clear</Button>
                  )}
                </div>

                {transactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No transactions</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Invoice</TableHead>
                        <TableHead className="text-xs">Service</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map(txn => (
                        <TableRow key={txn.id}>
                          <TableCell className="font-mono text-xs py-2">{txn.date ? format(parseISO(txn.date), 'MMM d, yy') : '-'}</TableCell>
                          <TableCell className="py-2">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${
                              txn.type === 'invoice' ? 'bg-purple-500/10 text-purple-600' :
                              txn.type === 'payment' ? 'bg-emerald-500/10 text-emerald-600' :
                              'bg-teal-500/10 text-teal-600'
                            }`}>{txn.type}</Badge>
                            {txn.is_prepayment && <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1 bg-teal-500/10 text-teal-600">PP</Badge>}
                          </TableCell>
                          <TableCell className="font-mono text-xs py-2">{txn.invoice_number || '-'}</TableCell>
                          <TableCell className="text-xs py-2 max-w-[120px] truncate">{txn.service || '-'}</TableCell>
                          <TableCell className={`text-right font-mono text-xs py-2 ${txn.amount_cents > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {txn.amount_cents > 0 ? '' : '-'}${(Math.abs(txn.amount_cents) / 100).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Select a client to view details</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
